import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar } from 'lucide-react';
import { API } from '../config';
import MetricDrillDownModal from './MetricDrillDownModal';
import SummaryDrillDownModal from './SummaryDrillDownModal';
import DashboardControls from './dashboard/DashboardControls';
import DailyDataView from './dashboard/DailyDataView';
import MtdDataView from './dashboard/MtdDataView';
import YtdDataView from './dashboard/YtdDataView';
import ComparePlantsView from './dashboard/ComparePlantsView';
import YearlyComparisonView from './dashboard/YearlyComparisonView';
import UtilizationView from './dashboard/UtilizationView';
import StoppageView from './dashboard/StoppageView';
import FyMatrixView from './dashboard/FyMatrixView';

const TREND_PARAMS_BY_VIEW_BUILDERS = {
  daily: ({ selectedDate }) => `view=daily&date_str=${selectedDate}`,
  mtd: ({ selectedYear, selectedMonth }) => `view=mtd&year=${selectedYear}&month=${selectedMonth}`,
  ytd: ({ selectedYear }) => `view=ytd&year=${selectedYear}`,
};

const DashboardView = ({ plants, selectedPlant: initialPlant, token }) => {
  // Landing default for the dashboard is always "All Plants (Consolidated)" so users see the
  // whole operation at a glance. Other views (Data Entry / Rates) still inherit the picked plant.
  const ALL_PLANTS = { id: 'all', name: 'All Plants (Consolidated)' };
  const today = new Date();
  // Default landing date is yesterday because today's shifts may not be fully entered yet.
  const yesterday = new Date(today.getTime() - 86400000);
  const [viewMode, setViewMode] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(yesterday.toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  // Default selectedYear to the current Indian Fiscal Year start (Apr-Mar). Jan-Mar belong to last calendar year's FY.
  const [selectedYear, setSelectedYear] = useState(today.getMonth() + 1 >= 4 ? today.getFullYear() : today.getFullYear() - 1);
  const [loading, setLoading] = useState(false);
  const [dailyData, setDailyData] = useState(null);
  const [mtdData, setMtdData] = useState(null);
  const [ytdData, setYtdData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [yearlyData, setYearlyData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [stoppageMins, setStoppageMins] = useState(0);
  const [stoppageInitialRange, setStoppageInitialRange] = useState(null);
  const [error, setError] = useState(null);
  const [dashboardPlant, setDashboardPlant] = useState(ALL_PLANTS);

  const [drillDownModal, setDrillDownModal] = useState({ isOpen: false, metricKey: null, metricLabel: null });
  const [summaryDrillDownModal, setSummaryDrillDownModal] = useState({ isOpen: false, summaryType: null, summaryLabel: null });

  const openDrillDown = (metricKey, metricLabel) => setDrillDownModal({ isOpen: true, metricKey, metricLabel });
  const closeDrillDown = () => setDrillDownModal({ isOpen: false, metricKey: null, metricLabel: null });
  const openSummaryDrillDown = (summaryType, summaryLabel) => setSummaryDrillDownModal({ isOpen: true, summaryType, summaryLabel });
  const closeSummaryDrillDown = () => setSummaryDrillDownModal({ isOpen: false, summaryType: null, summaryLabel: null });

  // When clicking a Stoppage tile, switch to the Stoppage view AND pre-fill its date range
  // to match the tile's period (single day for daily, full month for MTD, full year for YTD).
  const goToStoppageView = (range) => {
    setStoppageInitialRange(range);
    setViewMode('stoppage');
  };

  const stoppageRangeForView = () => {
    if (viewMode === 'daily') return { from: selectedDate, to: selectedDate };
    if (viewMode === 'mtd') {
      const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
      const mm = String(selectedMonth).padStart(2, '0');
      return { from: `${selectedYear}-${mm}-01`, to: `${selectedYear}-${mm}-${String(lastDay).padStart(2, '0')}` };
    }
    if (viewMode === 'ytd') {
      const today = new Date();
      const fyEnd = new Date(selectedYear + 1, 2, 31); // Mar 31 of next calendar year
      const endObj = today < fyEnd ? today : fyEnd;
      const endStr = endObj.toISOString().slice(0, 10);
      return { from: `${selectedYear}-04-01`, to: endStr };
    }
    return null;
  };

  const loadCompareData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/compare/${selectedDate}`, { headers: { Authorization: `Bearer ${token}` } });
      setCompareData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No comparison data available for this date'); setCompareData(null);
    } finally { setLoading(false); }
  }, [selectedDate, token]);

  const loadYearlyComparison = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/compare/yearly/${dashboardPlant?.id || 'all'}?years=2024,2025,2026`, { headers: { Authorization: `Bearer ${token}` } });
      setYearlyData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No yearly data available'); setYearlyData(null);
    } finally { setLoading(false); }
  }, [dashboardPlant, token]);

  const loadDashboardData = useCallback(async () => {
    setLoading(true); setError(null); setTrendData(null); setStoppageMins(0);
    const pid = dashboardPlant?.id;
    const authHeader = { headers: { Authorization: `Bearer ${token}` } };

    // Fire trend in parallel (best-effort: do not fail the view if trend fails)
    const builder = TREND_PARAMS_BY_VIEW_BUILDERS[viewMode];
    const trendParams = builder ? builder({ selectedDate, selectedMonth, selectedYear }) : null;
    const trendPromise = trendParams
      ? axios.get(`${API}/trend/${pid}?${trendParams}`, authHeader).then(r => r.data).catch(() => null)
      : Promise.resolve(null);

    // Determine stoppage date-range for the current view (best-effort, never fails the page).
    let stoppageRange = null;
    if (viewMode === 'daily') {
      stoppageRange = { from: selectedDate, to: selectedDate };
    } else if (viewMode === 'mtd') {
      const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
      const mm = String(selectedMonth).padStart(2, '0');
      stoppageRange = { from: `${selectedYear}-${mm}-01`, to: `${selectedYear}-${mm}-${String(lastDay).padStart(2, '0')}` };
    } else if (viewMode === 'ytd') {
      const today = new Date();
      const fyEnd = new Date(selectedYear + 1, 2, 31);
      const endObj = today < fyEnd ? today : fyEnd;
      stoppageRange = { from: `${selectedYear}-04-01`, to: endObj.toISOString().slice(0, 10) };
    }
    const stoppagePromise = stoppageRange
      ? axios.get(`${API}/stoppage/${pid}?from=${stoppageRange.from}&to=${stoppageRange.to}`, authHeader)
          .then(r => r.data?.totals?.stoppage_mins || 0).catch(() => 0)
      : Promise.resolve(0);

    try {
      if (viewMode === 'daily') {
        if (pid === 'all') {
          const res = await axios.get(`${API}/consolidated/daily/${selectedDate}`, authHeader);
          setDailyData({ ...res.data, is_consolidated: true });
        } else {
          const res = await axios.get(`${API}/daily/${pid}/${selectedDate}`, authHeader);
          setDailyData({ ...res.data, is_consolidated: false });
        }
      } else if (viewMode === 'mtd') {
        const res = await axios.get(`${API}/mtd/${pid}/${selectedYear}/${selectedMonth}`, authHeader);
        setMtdData(res.data);
      } else if (viewMode === 'ytd') {
        const res = await axios.get(`${API}/ytd/${pid}/${selectedYear}`, authHeader);
        setYtdData(res.data);
      }
      const trend = await trendPromise;
      if (trend) setTrendData(trend);
      const sMins = await stoppagePromise;
      setStoppageMins(sMins);
    } catch (err) {
      setError(err.response?.data?.detail || 'No data found for the selected period');
      setDailyData(null); setMtdData(null); setYtdData(null);
    } finally { setLoading(false); }
  }, [dashboardPlant, viewMode, selectedDate, selectedMonth, selectedYear, token]);

  useEffect(() => {
    if (viewMode === 'compare') loadCompareData();
    else if (viewMode === 'yearly') loadYearlyComparison();
    else if (viewMode === 'utilization' || viewMode === 'stoppage' || viewMode === 'fy_matrix') return; // self-fetching views
    else if (dashboardPlant) loadDashboardData();
  }, [viewMode, dashboardPlant, loadCompareData, loadYearlyComparison, loadDashboardData]);

  return (
    <div className="space-y-6">
      <DashboardControls plants={plants} dashboardPlant={dashboardPlant} setDashboardPlant={setDashboardPlant}
        viewMode={viewMode} setViewMode={setViewMode} selectedDate={selectedDate} setSelectedDate={setSelectedDate}
        selectedYear={selectedYear} setSelectedYear={setSelectedYear} selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth} />

      {loading && <div className="flex justify-center items-center h-32"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#156ACA]"></div></div>}

      {/* Show stale error only for views that depend on parent-loaded data. */}
      {error && !loading && viewMode !== 'utilization' && viewMode !== 'stoppage' && viewMode !== 'fy_matrix' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <Calendar className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
          <p className="text-gray-700">{error}</p>
          <p className="text-sm text-gray-500 mt-2">Try entering shift data first.</p>
        </div>
      )}

      {!loading && !error && viewMode === 'daily' && dailyData && <DailyDataView dailyData={dailyData} trendData={trendData} stoppageMins={stoppageMins} onStoppageClick={() => goToStoppageView(stoppageRangeForView())} openSummaryDrillDown={openSummaryDrillDown} openDrillDown={openDrillDown} />}
      {!loading && !error && viewMode === 'mtd' && mtdData && <MtdDataView mtdData={mtdData} trendData={trendData} stoppageMins={stoppageMins} onStoppageClick={() => goToStoppageView(stoppageRangeForView())} openSummaryDrillDown={openSummaryDrillDown} openDrillDown={openDrillDown} dashboardPlant={dashboardPlant} selectedYear={selectedYear} selectedMonth={selectedMonth} token={token} />}
      {!loading && !error && viewMode === 'ytd' && ytdData && <YtdDataView ytdData={ytdData} trendData={trendData} stoppageMins={stoppageMins} onStoppageClick={() => goToStoppageView(stoppageRangeForView())} openSummaryDrillDown={openSummaryDrillDown} openDrillDown={openDrillDown} />}
      {!loading && !error && viewMode === 'compare' && compareData && <ComparePlantsView compareData={compareData} />}
      {!loading && !error && viewMode === 'yearly' && yearlyData && <YearlyComparisonView yearlyData={yearlyData} />}
      {!loading && viewMode === 'utilization' && <UtilizationView dashboardPlant={dashboardPlant} selectedYear={selectedYear} token={token} />}
      {!loading && viewMode === 'stoppage' && <StoppageView dashboardPlant={dashboardPlant} selectedDate={selectedDate} selectedYear={selectedYear} selectedMonth={selectedMonth} token={token} initialFrom={stoppageInitialRange?.from} initialTo={stoppageInitialRange?.to} />}
      {!loading && viewMode === 'fy_matrix' && <FyMatrixView dashboardPlant={dashboardPlant} token={token} />}

      <MetricDrillDownModal isOpen={drillDownModal.isOpen} onClose={closeDrillDown} plantId={dashboardPlant?.id}
        plantName={dashboardPlant?.name} year={selectedYear} metricKey={drillDownModal.metricKey} metricLabel={drillDownModal.metricLabel} token={token} />
      <SummaryDrillDownModal isOpen={summaryDrillDownModal.isOpen} onClose={closeSummaryDrillDown} plantId={dashboardPlant?.id}
        plantName={dashboardPlant?.name} year={selectedYear} summaryType={summaryDrillDownModal.summaryType} summaryLabel={summaryDrillDownModal.summaryLabel} token={token} />
    </div>
  );
};

export default DashboardView;
